import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, clientAuth } from "@/lib/api";
import { PaidAssessments } from "@/components/client/PaidAssessments";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileText, ClipboardCheck, LogOut, Loader2, Activity, Calendar,
  ShoppingBag, CalendarCheck, Video, Lock, Clock,
} from "lucide-react";
import { toast } from "sonner";

const PURCHASE_URL = process.env.REACT_APP_PURCHASE_URL || "";
const BOOKING_URL = process.env.REACT_APP_BOOKING_URL || "";
const VIRTUAL_URL = process.env.REACT_APP_VIRTUAL_CONSULT_URL || "";

const NextStepCard = ({ icon: Icon, title, body, url, testId }) => {
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="kcis-card p-5 flex items-start gap-4 hover:border-purple-300 transition group"
      data-testid={testId}
    >
      <div className="h-10 w-10 rounded-xl bg-purple-50 text-purple-900 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="font-display text-base text-slate-900 group-hover:text-purple-900">{title}</div>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed">{body}</p>
      </div>
    </a>
  );
};

const FollowupAction = ({ assessmentId }) => {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/assessments/${assessmentId}/followup-eligibility`);
        if (!cancelled) setInfo(data);
      } catch {
        // eligibility check is non-fatal — show the button optimistically
        if (!cancelled) setInfo({ eligible: true });
      }
    })();
    return () => { cancelled = true; };
  }, [assessmentId]);

  if (!info) {
    return (
      <Button disabled variant="outline" className="rounded-full border-slate-300" data-testid={`client-followup-loading-${assessmentId}`}>
        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Follow-up
      </Button>
    );
  }

  if (info.eligible) {
    return (
      <Link to={`/client/followup/${assessmentId}`}>
        <Button className="rounded-full bg-purple-900 hover:bg-purple-800 text-white" data-testid={`client-followup-${assessmentId}`}>
          <ClipboardCheck className="h-4 w-4 mr-2" /> 7-day follow-up
        </Button>
      </Link>
    );
  }

  const reasonText =
    info.reason === "submitted" ? "Follow-up completed" :
    info.reason === "window_expired" ? "Follow-up window expired" :
    "Follow-up unavailable";
  const ReasonIcon = info.reason === "submitted" ? ClipboardCheck : Clock;
  return (
    <Button
      disabled
      variant="outline"
      className="rounded-full border-slate-200 text-slate-500 bg-slate-50"
      data-testid={`client-followup-${info.reason}-${assessmentId}`}
    >
      <ReasonIcon className="h-4 w-4 mr-2" /> {reasonText}
    </Button>
  );
};

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = clientAuth.getId();
    if (!id) {
      navigate("/client/login");
      return;
    }
    (async () => {
      try {
        const [{ data: c }, { data: a }, { data: f }] = await Promise.all([
          api.get(`/clients/${id}`),
          api.get(`/clients/${id}/assessments`),
          api.get(`/clients/${id}/followups`),
        ]);
        setClient(c);
        setAssessments(a);
        setFollowups(f);
      } catch {
        toast.error("Session expired");
        clientAuth.clear();
        navigate("/client/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-purple-900" /></div>;
  }

  const anyNextStepUrl = !!(PURCHASE_URL || BOOKING_URL || VIRTUAL_URL);

  return (
    <div className="min-h-screen flex flex-col">
      <TopNav
        right={
          <Button
            variant="ghost"
            className="rounded-full text-slate-700"
            onClick={() => { clientAuth.clear(); navigate("/"); }}
            data-testid="client-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        }
      />
      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
        <div className="kcis-fade-up">
          <div className="kcis-overline text-purple-900">Welcome back</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-slate-900 mt-2">
            {client?.name}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            {client?.email} · joined {new Date(client?.created_at).toLocaleDateString()}
          </p>
        </div>

        {/* Care Hub quick links */}
        <div className="mt-6 flex flex-wrap gap-3" data-testid="care-hub-links">
          <Link to="/client/programs">
            <Button className="rounded-full bg-purple-900 hover:bg-purple-800 text-white" data-testid="client-programs-link">My Programs</Button>
          </Link>
        </div>

        <PaidAssessments />

        {/* Assessments */}
        <div className="mt-10">
          <div className="kcis-overline mb-4">Your assessments</div>
          {assessments.length === 0 ? (
            <div className="kcis-card p-10 text-center" data-testid="client-empty-state">
              <FileText className="h-8 w-8 mx-auto text-slate-400" />
              <p className="mt-3 text-slate-600">No assessments on file yet.</p>
              <p className="mt-1 text-xs text-slate-500">
                Once you've completed a paid assessment, it'll show up here with your wellness report.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {assessments.map((a) => {
                const aFollowups = followups.filter((f) => f.assessment_id === a.id);
                return (
                  <div key={a.id} className="kcis-card p-6" data-testid={`client-assessment-${a.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 text-slate-500 text-xs">
                          <Calendar className="h-3.5 w-3.5" />
                          {new Date(a.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                        </div>
                        <h3 className="font-display text-lg text-slate-900 mt-1">{a.primary_concern || "Wellness assessment"}</h3>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <Badge className={`text-xs border ${a.report_status === "generated" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                            Report: {a.report_status === "generated" ? "Ready" : "Pending"}
                          </Badge>
                          <Badge className={`text-xs border ${a.followup_status === "submitted" ? "bg-purple-50 text-purple-900 border-purple-100" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                            Follow-up: {a.followup_status === "submitted" ? "Submitted" : "Not submitted"}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Link to={`/report/${a.id}`}>
                          <Button variant="outline" className="rounded-full border-slate-300" data-testid={`client-view-report-${a.id}`}>
                            <FileText className="h-4 w-4 mr-2" /> View report
                          </Button>
                        </Link>
                        <FollowupAction assessmentId={a.id} />
                      </div>
                    </div>

                    {aFollowups.length > 0 && (
                      <div className="mt-5 border-t border-slate-100 pt-4">
                        <div className="kcis-overline mb-2">Follow-up history</div>
                        <div className="space-y-2">
                          {aFollowups.map((f) => (
                            <div key={f.id} className="bg-slate-50/60 rounded-lg p-3 flex flex-wrap items-center gap-3 justify-between text-sm">
                              <div className="flex items-center gap-2 text-slate-700">
                                <Activity className="h-3.5 w-3.5 text-purple-900" />
                                {new Date(f.submitted_at).toLocaleString()}
                              </div>
                              <div className="flex flex-wrap gap-1.5 text-xs">
                                <Badge variant="secondary" className="bg-white border border-slate-200">Energy {f.energy_level}/10</Badge>
                                <Badge variant="secondary" className="bg-white border border-slate-200">Sleep {f.sleep_quality}/10</Badge>
                                <Badge variant="secondary" className="bg-white border border-slate-200">Stress {f.stress_level}/10</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Next steps — paid */}
        <div className="mt-12" data-testid="client-next-steps">
          <div className="kcis-overline">Continue your wellness journey</div>
          <h2 className="font-display text-xl tracking-tight text-slate-900 mt-2">Next steps</h2>
          <p className="text-sm text-slate-600 mt-1">
            New assessments are part of a paid plan. Choose how you'd like to continue:
          </p>
          {anyNextStepUrl ? (
            <div className="grid md:grid-cols-3 gap-4 mt-5">
              <NextStepCard
                icon={ShoppingBag}
                title="Purchase new assessment"
                body="Start a new KISSED Wellness Assessment™ — each assessment is a separate purchase."
                url={PURCHASE_URL}
                testId="cta-purchase-assessment"
              />
              <NextStepCard
                icon={CalendarCheck}
                title="Book an appointment"
                body="See your KISSED practitioner in-clinic for a personalised review."
                url={BOOKING_URL}
                testId="cta-book-appointment"
              />
              <NextStepCard
                icon={Video}
                title="Schedule virtual consultation"
                body="A focused 1:1 video session with your KISSED practitioner."
                url={VIRTUAL_URL}
                testId="cta-virtual-consult"
              />
            </div>
          ) : (
            <div className="kcis-card p-6 mt-5 flex items-start gap-3 text-sm text-slate-600">
              <Lock className="h-4 w-4 mt-0.5 text-purple-900" />
              Your KISSED practitioner will share next-step links here soon. In the meantime, please contact us directly to schedule.
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
